import barba from '@barba/core';
import { initializeComponents } from '../index.js';
import { refreshAOS } from './aos.js';
import {initializeLatestIssues} from './latest-issues.js';
// import gsap from 'gsap';
import { destroyAllSwipers, clearHeroContentCache } from './swiper-utils.js';

/**
 * Initialize Barba.js for page transitions
 */
export const initializeBarba = () => {
  // Verify Barba wrapper exists
  const barbaWrapper = document.querySelector('[data-barba="wrapper"]');
  if (!barbaWrapper) {
    console.error('Barba.js: No wrapper element found. Please add a div with data-barba="wrapper" attribute to your HTML.');
    return;
  }

  // Store the scroll position before transition
  let scrollPosition = 0;
  let loadingIndicator;
  let pendingHash = null;

  // Create loading indicator
  const createLoadingIndicator = () => {
    const mainContent = document.querySelector('main');
    if (mainContent) {
      loadingIndicator = document.createElement('div');
      loadingIndicator.className = 'loading-indicator';
      mainContent.appendChild(loadingIndicator);
    }
  };

  // Remove loading indicator
  const removeLoadingIndicator = () => {
    if (loadingIndicator) {
      loadingIndicator.remove();
      loadingIndicator = null;
    }
  };

  barba.init({
    // Prevent click when transition is running
    preventRunning: true,

    // Timeout for transitions (in milliseconds)
    timeout: 5000,

    // Enable proper browser history handling
    historyApi: true,

    // Custom URL processing to handle hash preservation
    url: {
      // Custom function to get the URL for navigation
      get: href => {
        // If the href contains a hash, strip it for barba processing
        // The hash will be handled separately
        return href.split('#')[0];
      },
    },

    // Prevent Barba from running on same page
    prevent: ({ el }) => {
      // Don't run Barba if clicking on the same page
      return el.href === window.location.href;
    },

    // Prevent Barba from running on external links
    preventCustom: ({ el }) => {
      // Don't run Barba on external links
      if (el.hostname !== window.location.hostname) {
        return true;
      }

      // Prevent barba on media/static files
      const path = el.pathname.toLowerCase();

      if (
        path.match(/\.(jpg|jpeg|png|gif|webp|svg|pdf|mp4|webm)$/)
      ) {
        return true;
      }

      // For same-page anchor links (hash only), let browser handle it
      if (el.hash && el.pathname === window.location.pathname) {
        return true;
      }

      // For cross-page links with hash, we need special handling
      if (el.hash && el.pathname !== window.location.pathname) {
        // Store the hash for later use
        el.setAttribute('data-target-hash', el.hash);
        // Temporarily modify href to remove hash for Barba processing
        const originalHref = el.href;
        el.setAttribute('data-original-href', originalHref);
        el.href = el.href.split('#')[0];
        // Allow Barba to handle the modified URL
        return false;
      }

      return false;
    },
  });

  barba.hooks.after(() => {
    // Remove loading state class from body
    document.body.classList.remove('is-transitioning');

    // Handle hash navigation from pending hash or current URL
    const hashToUse = pendingHash || window.location.hash;

    if (hashToUse) {
      // Update URL with hash if we have a pending hash
      if (pendingHash && !window.location.hash) {
        const newUrl = window.location.href + pendingHash;
        window.history.replaceState(null, '', newUrl);
      }

      // Wait for DOM to be ready, then scroll to the hash target
      setTimeout(() => {
        const targetElement = document.querySelector(hashToUse);
        if (targetElement) {
          const headerOffset = 250; // Adjust based on your header height
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 200);

      // Clear pending hash after use
      pendingHash = null;
    }

    else if (!sessionStorage.getItem('scrollToFilters')) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Initialize components on the new page with a small delay
    setTimeout(async () => {
      await initializeComponents();
      // initializeLatestIssues();
      // Refresh AOS to ensure it picks up new elements
      refreshAOS();
      removeLoadingIndicator();
    }, 100);

    // Additional AOS refresh after a longer delay to catch any missed animations
    setTimeout(() => {
      refreshAOS();
    }, 500);

    // Add scroll event listener for this page to catch missed animations
    const handlePageScroll = () => {
      // Refresh AOS when user scrolls on the new page
      refreshAOS();
      // Remove the listener after first scroll to avoid performance issues
      window.removeEventListener('scroll', handlePageScroll);
    };

    // Listen for scroll events on the new page
    window.addEventListener('scroll', handlePageScroll, { passive: true });

    // Ensure proper scroll restoration for browser navigation
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }

    //rerun scripts
    document.querySelectorAll('.widget-embed, .rte-content').forEach(widget => {
      const scripts = widget.querySelectorAll('script');

      scripts.forEach(oldScript => {
        const newScript = document.createElement('script');

        [...oldScript.attributes].forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });

        if (oldScript.src) {
          newScript.src = oldScript.src;
        } else {
          newScript.textContent = oldScript.textContent;
        }

        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    });
  });

  barba.hooks.before((data) => {
    // Store scroll position
    scrollPosition = window.scrollY;
    createLoadingIndicator();

    // Destroy all Swiper instances to prevent conflicts
    destroyAllSwipers();

    // Capture hash from the clicked link's stored attributes
    if (data.trigger && data.trigger.getAttribute) {
      const targetHash = data.trigger.getAttribute('data-target-hash');
      const originalHref = data.trigger.getAttribute('data-original-href');

      if (targetHash) {
        pendingHash = targetHash;
        // Restore original href after capturing hash
        if (originalHref) {
          data.trigger.href = originalHref;
        }
      } else {
        pendingHash = null;
      }
    } else {
      pendingHash = null;
    }

    // Additional handling for navigation if needed
    const trigger = data.trigger;
    if (trigger && trigger.getAttribute && trigger.getAttribute('data-target-hash')) {
      // Ensure pendingHash is captured from click
      if (!pendingHash) {
        pendingHash = trigger.getAttribute('data-target-hash');
      }
    }

    // Clear hero content cache before any navigation
    clearHeroContentCache();
  });

  // Handle hash links on hover and click
  document.addEventListener('mouseover', event => {
    const link = event.target.closest('a');
    if (link && link.href && link.hash && link.hostname === window.location.hostname) {
      // Prepare hash link for potential barba processing
      if (link.pathname !== window.location.pathname && !link.getAttribute('data-target-hash')) {
        link.setAttribute('data-target-hash', link.hash);
        link.setAttribute('data-original-href', link.href);
      }
    }
  });
};